import * as React from "react";
import { Search } from "lucide-react";
import { InputField } from "@/shared/ui/components/custom/InputField";
import { Label } from "@/shared/ui/components/ui/label";
import { Button } from "@/shared/ui/components/custom/Button";
import {
  Select,
  type SelectOption,
} from "@/shared/ui/components/custom/Select";
import { cn } from "@/shared/lib/utils";

export type SearchInputVariant =
  | "default"
  | "button-inside"
  | "select-button"
  | "input-only";
export type SearchInputSize = "sm" | "md" | "lg";

export interface SearchInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> {
  variant?: SearchInputVariant;
  size?: SearchInputSize;
  label?: string;
  placeholder?: string;
  buttonLabel?: string;
  showButtonLabel?: boolean;
  onSearch?: (value: string) => void;
  selectOptions?: SelectOption[];
  selectValue?: string;
  onSelectChange?: (value: string) => void;
  selectPlaceholder?: string;
  error?: string;
  onErrorClear?: () => void;
  disabled?: boolean;
  showButton?: boolean;
}

const sizeClasses = {
  sm: {
    input: "h-10 px-2 py-4 text-sm",
    inputInside: "h-12 px-2 py-4 text-sm",
    button: "h-8 p-2 text-sm rounded-lg",
    select: "!h-full px-3 py-1.5 text-sm",
    icon: "w-4 h-4",
    gap: "gap-2",
  },
  md: {
    input: "h-12 px-2 py-3 text-sm",
    inputInside: "h-[68px] px-2 py-4 text-sm",
    button: "h-11 p-3 text-sm rounded-xl",
    select: "!h-full px-4 py-2.5 text-sm",
    icon: "w-4 h-4",
    gap: "gap-3",
  },
  lg: {
    input: "px-2 text-base h-14  py-4 md:text-base",
    inputInside: "px-2 py-4 text-base h-[88px] md:text-base",
    button: " w-8 p-2 text-base h-14 p-4 md:text-base rounded-xl",
    select: "!h-full px-5 text-base md:px-5 py-[15px] md:text-base",
    icon: "w-5 h-5 md:w-5 md:h-5",
    gap: "gap-4",
  },
};

const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      variant = "default",
      size = "md",
      label,
      placeholder = "Search...",
      buttonLabel = "Search",
      showButtonLabel = false,
      showButton = true,
      onSearch,
      selectOptions = [],
      selectValue,
      onSelectChange,
      selectPlaceholder,
      error,
      onErrorClear,
      disabled,
      className,
      value,
      onChange,
      ...props
    },
    ref
  ) => {
    const [searchValue, setSearchValue] = React.useState<string>(
      (value as string) || ""
    );
    const inputRef = React.useRef<HTMLInputElement>(null);

    React.useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    const sizeConfig = sizeClasses[size];
    const hasError = Boolean(error);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setSearchValue(newValue);
      onErrorClear?.();
      onChange?.(e);
    };

    const handleSearch = () => {
      onSearch?.(searchValue);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        handleSearch();
      }
      props.onKeyDown?.(e);
    };

    const handleSelectChange = (value: string) => {
      onErrorClear?.();
      onSelectChange?.(value);
    };

    const SearchIcon = <Search className={cn(sizeConfig.icon)} />;

    const buttonContent =
      showButtonLabel &&
      variant !== "button-inside" &&
      variant !== "select-button" &&
      variant !== "input-only" ? (
        <>
          <Search className={cn(sizeConfig.icon)} />
          {buttonLabel}
        </>
      ) : (
        <Search className={cn(sizeConfig.icon)} />
      );

    const inputBaseClasses = cn(
      "w-full rounded-lg border border-grey-100 bg-white font-normal shadow-none transition-colors",
      "focus-visible:ring-0",
      "text-grey-200 placeholder:text-grey-200",
      "hover:border-brand-green hover:text-grey-200",
      "focus-visible:border-brand-green focus-visible:text-grey-900",
      "disabled:cursor-not-allowed",
      hasError &&
        "bg-error-bg border-error-700 text-error-700 placeholder:text-error-700 hover:border-error-700 focus-visible:border-error-700 focus-visible:text-error-700"
    );

    // Case 1: Default - Button outside
    if (variant === "default") {
      return (
        <div className={cn("w-full", className)}>
          {label && (
            <Label
              className={cn(
                "mb-3 block text-sm font-medium text-grey-900",
                disabled && "text-grey-200",
                hasError && "text-error-700"
              )}
            >
              {label}
            </Label>
          )}
          <div
            className={cn(
              "flex gap-2",
              hasError ? "items-start" : "items-center",
              sizeConfig.gap
            )}
          >
            <div className="relative flex-1">
              <InputField
                ref={inputRef}
                type="text"
                value={searchValue}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                disabled={disabled}
                placeholder={placeholder}
                leftIcon={SearchIcon}
                error={error}
                onErrorClear={onErrorClear}
                className={cn(
                  inputBaseClasses,
                  sizeConfig.input,
                  searchValue && "text-grey-900"
                )}
                {...props}
              />
            </div>
            {showButton && (
              <Button
                variant="black"
                size={size === "sm" ? "sm" : size === "lg" ? "lg" : "default"}
                onClick={handleSearch}
                disabled={disabled}
                className={cn(
                  sizeConfig.button,
                  "whitespace-nowrap w-fit ",
                  hasError && "mt-0"
                )}
              >
                {buttonContent}
              </Button>
            )}
          </div>
        </div>
      );
    }

    // Case 2: Button inside input
    if (variant === "button-inside") {
      return (
        <div className={cn("w-full", className)}>
          <div className="relative [&>div>div]:relative">
            <InputField
              ref={inputRef}
              type="text"
              value={searchValue}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              disabled={disabled}
              placeholder={placeholder}
              leftIcon={SearchIcon}
              error={error}
              onErrorClear={onErrorClear}
              className={cn(
                inputBaseClasses,
                sizeConfig.inputInside,
                "[&_input]:pe-24 [&_input]:rtl:ps-24 [&_input]:rtl:pe-10",
                "[&_input]:py-4 [&_input]:px-2",
                searchValue && "[&_input]:text-grey-900"
              )}
              {...props}
            />
            <Button
              variant="black"
              size={size === "sm" ? "sm" : size === "lg" ? "lg" : "default"}
              onClick={handleSearch}
              disabled={disabled}
              className={cn(
                "absolute end-2 ",
                hasError ? "top-2" : "top-1/2 -translate-y-1/2",
                sizeConfig.button,
                "whitespace-nowrap"
              )}
            >
              {buttonContent}
            </Button>
          </div>
        </div>
      );
    }

    // Case 3: Input only (no button) - can optionally have select
    if (variant === "input-only") {
      return (
        <div className={cn("w-full", className)}>
          {label && (
            <Label
              className={cn(
                "mb-3 block text-sm font-medium text-grey-900",
                disabled && "text-grey-200",
                hasError && "text-error-700"
              )}
            >
              {label}
            </Label>
          )}
          <div
            className={cn(
              "flex items-center gap-0 rounded-lg border transition-colors",
              "border-grey-100 bg-white",
              "hover:border-brand-green",
              "focus-within:border-brand-green",
              hasError &&
                "bg-error-bg border-error-700 hover:border-error-700 focus-within:border-error-700",
              disabled &&
                "bg-grey-50 border-grey-100 cursor-not-allowed opacity-50",
              size === "sm" && "h-10",
              size === "md" && "h-12",
              size === "lg" && "h-14",
              "rtl:flex-row-reverse"
            )}
          >
            {selectOptions.length > 0 && (
              <>
                <div className="flex-shrink-0 h-full [&>div]:h-full [&>div>div]:h-full">
                  <Select
                    options={selectOptions}
                    value={selectValue}
                    onChange={handleSelectChange}
                    placeholder={selectPlaceholder}
                    disabled={disabled}
                    className={cn(
                      "!border-0 !rounded-l-lg !rounded-r-none !rtl:rounded-l-none !rtl:rounded-r-lg",
                      "!bg-grey-50 !text-grey-500 !w-fit",
                      "focus-visible:ring-0 focus-visible:outline-none",
                      "h-full",
                      hasError && "!text-error-700 !bg-error-bg",
                      sizeConfig.select
                    )}
                  />
                </div>

                <div
                  className={cn(
                    "w-px h-full bg-grey-200 flex-shrink-0",
                    hasError && "bg-error-700"
                  )}
                />
              </>
            )}

            <div className="flex-1 min-w-0 h-full [&>div]:h-full [&>div>div]:h-full">
              <InputField
                ref={inputRef}
                type="text"
                value={searchValue}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                disabled={disabled}
                placeholder={placeholder}
                leftIcon={!selectOptions.length ? SearchIcon : undefined}
                error={error}
                onErrorClear={onErrorClear}
                className={cn(
                  "!border-0",
                  selectOptions.length > 0 ? "!rounded-none" : "!rounded-lg",
                  "!h-full",
                  inputBaseClasses,
                  sizeConfig.input,
                  searchValue && "text-grey-900",
                  "[&>div]:gap-0 [&>div>div]:relative [&>div>div]:h-full",
                  "[&>div>div>input]:border-0 [&>div>div>input]:rounded-none [&>div>div>input]:h-full",
                  "[&>div>div>input]:focus-visible:ring-0 [&>div>div>input]:focus-visible:outline-none",
                  "[&>div>div>input]:shadow-none"
                )}
                {...props}
              />
            </div>
          </div>
          {error && (
            <p className="mt-3 text-xs font-medium text-error-700">{error}</p>
          )}
        </div>
      );
    }

    // Case 4: Select + Button inside input (single unified control)
    return (
      <div className={cn("w-full", className)}>
        <div
          className={cn(
            "flex items-center gap-0 rounded-lg border transition-colors",
            "border-grey-100 bg-white",
            "hover:border-brand-green",
            "focus-within:border-brand-green",
            hasError &&
              "bg-error-bg border-error-700 hover:border-error-700 focus-within:border-error-700",
            disabled &&
              "bg-grey-50 border-grey-100 cursor-not-allowed opacity-50",
            size === "sm" && "h-10",
            size === "md" && "h-12",
            size === "lg" && "h-14",
            "rtl:flex-row-reverse"
          )}
        >
          {selectOptions.length > 0 && (
            <>
              <div className="flex-shrink-0 h-full [&>div]:h-full [&>div>select]:h-full">
                <Select
                  options={selectOptions}
                  value={selectValue}
                  onChange={handleSelectChange}
                  placeholder={selectPlaceholder}
                  disabled={disabled}
                  className={cn(
                    "!border-0 !rounded-l-lg !rounded-r-none !rtl:rounded-l-none !rtl:rounded-r-lg",
                    "!bg-grey-50 !text-grey-500 !w-fit",
                    "focus-visible:ring-0 focus-visible:outline-none",
                    "h-full",
                    hasError && "!text-error-700 !bg-error-bg",
                    sizeConfig.select
                  )}
                />
              </div>

              <div
                className={cn(
                  "w-px h-full bg-grey-200 flex-shrink-0",
                  hasError && "bg-error-700"
                )}
              />
            </>
          )}

          <div className="flex-1 min-w-0 h-full [&>div]:h-full [&>div>div]:h-full">
            <InputField
              ref={inputRef}
              type="text"
              value={searchValue}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              disabled={disabled}
              placeholder={placeholder}
              leftIcon={!selectOptions.length ? SearchIcon : undefined}
              onErrorClear={onErrorClear}
              className={cn(
                "!border-0 !rounded-none",
                "!h-full",
                inputBaseClasses,
                sizeConfig.input,
                searchValue && "text-grey-900",
                "[&>div]:gap-0 [&>div>div]:relative [&>div>div]:h-full",
                "[&>div>div>input]:border-0 [&>div>div>input]:rounded-none [&>div>div>input]:h-full",
                "[&>div>div>input]:focus-visible:ring-0 [&>div>div>input]:focus-visible:outline-none",
                "[&>div>div>input]:shadow-none"
              )}
              {...props}
            />
          </div>

          <div className="flex-shrink-0 h-full flex">
            <Button
              variant="black"
              onClick={handleSearch}
              disabled={disabled}
              style={{
                height: "100%",
              }}
              className={cn(
                "!border-0 !rounded-l-none !rounded-r-lg !rtl:rounded-l-lg !rtl:rounded-r-none",
                size === "sm" && "p-2 text-sm w-11",
                size === "md" && "p-3 text-sm w-14",
                size === "lg" && " text-base w-16 p-4 md:text-base",
                "whitespace-nowrap",
                "flex items-center justify-center"
              )}
            >
              {buttonContent}
            </Button>
          </div>
        </div>
        {error && (
          <p className="mt-3 text-xs font-medium text-error-700">{error}</p>
        )}
      </div>
    );
  }
);

SearchInput.displayName = "SearchInput";

export { SearchInput };
